/**
 * REST client. Access token in memory; refresh token in an httpOnly cookie (credentials: include).
 * A session opened from the CRM ("Log in as client") keeps its refresh token in this tab instead, see
 * staffSession().
 */
import { API_URL } from './config';
import { geoStore } from './geo';
import { ws } from './ws';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** The rest of the error body, e.g. a refused dealer order's `help`. */
    public data?: Record<string, unknown>,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
const tokenListeners = new Set<(t: string | null) => void>();

export function setAccessToken(t: string | null) {
  accessToken = t;
  ws.setToken(t);
  for (const l of tokenListeners) l(t);
}
export const getAccessToken = () => accessToken;
export function onTokenChange(l: (t: string | null) => void) {
  tokenListeners.add(l);
  return () => tokenListeners.delete(l);
}

/**
 * A session opened from the CRM ("Log in as client") belongs to this tab. Its refresh token lives in
 * sessionStorage, which other tabs don't see and which dies with the tab, instead of the browser-wide
 * cookie: the staff member's own Platform session in another tab is left alone, two clients can be
 * open side by side, and closing the tab ends the session (the API's worker confirms it within a
 * minute; see the pagehide handler in stores/session.ts).
 */
export interface StaffSession {
  refreshToken: string;
  /** When the session ends whatever happens (CRM_LOGIN_SESSION_MINUTES after it started). */
  expiresAt: string;
  signedInBy: string;
}
const STAFF_KEY = 'staffSession';

export function staffSession(): StaffSession | null {
  try {
    const raw = sessionStorage.getItem(STAFF_KEY);
    return raw ? (JSON.parse(raw) as StaffSession) : null;
  } catch {
    return null;
  }
}

export function setStaffSession(s: StaffSession | null) {
  try {
    if (s) sessionStorage.setItem(STAFF_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(STAFF_KEY);
  } catch {
    /* no storage: the session lasts as long as the access token */
  }
}

/** Single-flight refresh. Resolves to the new token or null. */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = refreshOnce()
      .catch(() => null)
      .then((t) => {
        setAccessToken(t);
        return t;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

/** This tab's staff session first; once that is over, the browser's own cookie session. */
async function refreshOnce(): Promise<string | null> {
  const staff = staffSession();
  if (staff) {
    const r = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: staff.refreshToken }),
      credentials: 'include',
    });
    if (r.ok) {
      const j = (await r.json()) as { accessToken: string; refreshToken?: string; expiresAt?: string };
      if (j.refreshToken) setStaffSession({ ...staff, refreshToken: j.refreshToken, expiresAt: j.expiresAt ?? staff.expiresAt });
      return j.accessToken;
    }
    // The staff session is over: signed out, ended by the API after the tab was gone, or the hour is up.
    setStaffSession(null);
  }
  const r = await fetch(`${API_URL}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
  if (r.status === 403) flagGeo(await r.json().catch(() => null));
  if (!r.ok) return null;
  const j = (await r.json()) as { accessToken: string };
  return j.accessToken;
}

/** The API refuses everything from a restricted country with `geo_blocked`: show the popup. */
function flagGeo(data: { error?: string } | null | undefined) {
  if (data?.error === 'geo_blocked') geoStore.set({ refused: true });
}

interface Opts {
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function request<T>(method: string, path: string, body?: unknown, opts: Opts = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = {};
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      credentials: 'include',
      signal: opts.signal,
    });
  } catch {
    throw new ApiError(0, 'network', 'Network error — check your connection');
  }
  if (res.status === 401 && !retried && !path.startsWith('/api/auth/')) {
    const t = await refreshAccessToken();
    if (t) return request<T>(method, path, body, opts, true);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (res.status === 403) flagGeo(data);
  if (!res.ok) throw new ApiError(res.status, data?.error ?? 'error', data?.message ?? `Request failed (${res.status})`, data && typeof data === 'object' ? data : undefined);
  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: Opts) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: Opts) => request<T>('POST', path, body ?? {}, opts),
  patch: <T>(path: string, body?: unknown, opts?: Opts) => request<T>('PATCH', path, body ?? {}, opts),
  put: <T>(path: string, body?: unknown, opts?: Opts) => request<T>('PUT', path, body ?? {}, opts),
  del: <T>(path: string, opts?: Opts) => request<T>('DELETE', path, undefined, opts),
  /** multipart/form-data upload (files + fields). */
  upload: <T>(path: string, form: FormData, opts?: Opts) => request<T>('POST', path, form, opts),
};

/** Download a protected file (needs the Bearer token, so a plain link won't do) and save it. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const go = () => fetch(`${API_URL}${path}`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}, credentials: 'include' });
  let res = await go();
  if (res.status === 401 && (await refreshAccessToken())) res = await go();
  if (!res.ok) throw new ApiError(res.status, 'download_failed', 'Could not download the file');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
